/**
 * Custom hooks for CompanyDetailPage
 * Extracted to improve maintainability and reusability
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAppMessage } from '../components/AppMessage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Parse shorthand values like 50M, 1.5B, 500K to numbers
 */
export const parseShorthandValue = (value) => {
  if (!value) return null;
  const str = String(value).toUpperCase().trim();
  
  if (!isNaN(parseFloat(str)) && isFinite(str)) {
    return parseFloat(str);
  }
  
  const match = str.match(/^([\d.]+)\s*([KMB]?)$/);
  if (match) {
    const num = parseFloat(match[1]);
    const suffix = match[2];
    switch (suffix) {
      case 'K': return num * 1000;
      case 'M': return num * 1000000;
      case 'B': return num * 1000000000;
      default: return num;
    }
  }
  
  return null;
};

/**
 * Format numbers as shorthand (50M, 1.5B, 500K)
 */
export const formatShorthand = (value) => {
  if (!value) return '';
  const num = parseFloat(value);
  if (isNaN(num)) return '';
  
  if (num >= 1000000000) {
    return (num / 1000000000).toFixed(num % 1000000000 === 0 ? 0 : 1).replace(/\.0$/, '') + 'B';
  } else if (num >= 1000000) {
    return (num / 1000000).toFixed(num % 1000000 === 0 ? 0 : 1).replace(/\.0$/, '') + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(num % 1000 === 0 ? 0 : 1).replace(/\.0$/, '') + 'K';
  }
  return num.toString();
};

/**
 * Hook for fetching and managing company data
 */
export const useCompanyData = (companyId, navigate) => {
  const [company, setCompany] = useState(null);
  const [leads, setLeads] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLocationId, setSelectedLocationId] = useState(null);

  const loadData = useCallback(async () => {
    if (!companyId) return;
    
    setLoading(true);
    try {
      const [companyRes, leadsRes] = await Promise.all([
        axios.get(`${API}/companies/${companyId}`),
        axios.get(`${API}/leads?company_id=${companyId}&limit=100`)
      ]);
      
      setCompany(companyRes.data);
      setLeads(leadsRes.data.items || []);
      
      // Get communications for all leads
      const allComms = [];
      for (const lead of (leadsRes.data.items || [])) {
        try {
          const commsRes = await axios.get(`${API}/communications/lead/${lead.id}`);
          allComms.push(...(commsRes.data || []).map(c => ({
            ...c,
            lead_name: `${lead.first_name} ${lead.last_name || ''}`.trim(),
            lead_id: lead.id,
            location_id: lead.location_id
          })));
        } catch (e) {
          // Ignore errors for individual lead communications
        }
      }
      allComms.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setCommunications(allComms);
      
    } catch (error) {
      toast.error('Failed to load company');
      navigate('/admin/companies');
    } finally {
      setLoading(false);
    }
  }, [companyId, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleLocationClick = useCallback((locationId) => {
    setSelectedLocationId(prev => prev === locationId ? null : locationId);
  }, []);

  const getSelectedLocationName = useCallback(() => {
    if (!selectedLocationId) return null;
    const loc = company?.locations?.find(l => l.id === selectedLocationId);
    return loc?.name || null;
  }, [selectedLocationId, company]);

  // Filter by selected location
  const filteredLeads = selectedLocationId 
    ? leads.filter(l => l.location_id === selectedLocationId)
    : leads;
    
  const filteredCommunications = selectedLocationId
    ? communications.filter(c => c.location_id === selectedLocationId)
    : communications;

  return {
    company,
    leads,
    communications,
    loading,
    selectedLocationId,
    setSelectedLocationId,
    loadData,
    handleLocationClick,
    getSelectedLocationName,
    filteredLeads,
    filteredCommunications
  };
};

/**
 * Hook for location editing
 */
export const useLocationEditor = (company, companyId, loadData) => {
  const [editLocationOpen, setEditLocationOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [editingLocationIndex, setEditingLocationIndex] = useState(null);
  const [saving, setSaving] = useState(false);
  const msg = useAppMessage();

  const openEditLocation = useCallback((location, index, e) => {
    e?.stopPropagation();
    setEditingLocation({ ...location });
    setEditingLocationIndex(index);
    setEditLocationOpen(true);
  }, []);

  const openAddLocation = useCallback(() => {
    setEditingLocation({ id: '', name: '', address: '', city: '', state: '', zip: '', phone: '' });
    setEditingLocationIndex(-1);
    setEditLocationOpen(true);
  }, []);

  const handleSaveLocation = useCallback(async () => {
    if (!editingLocation?.name) {
      toast.error('Location name is required');
      return;
    }
    setSaving(true);
    try {
      const updatedLocations = [...(company?.locations || [])];

      if (editingLocationIndex === -1) {
        const newLocation = { ...editingLocation, id: crypto.randomUUID() };
        updatedLocations.push(newLocation);
      } else {
        updatedLocations[editingLocationIndex] = editingLocation;
      }

      await axios.put(`${API}/companies/${companyId}`, {
        name: company.name,
        website: company.website || null,
        locations: updatedLocations,
        ats_used: company.ats_used || null,
        ai_voice_using: company.ai_voice_using || null,
        other_ai_tools: company.other_ai_tools || null,
        company_type: company.company_type || 'potential',
        total_revenue: company.total_revenue || null,
        total_jobs: company.total_jobs || null,
        company_notes: company.company_notes || null,
        num_recruiters: company.num_recruiters || null,
        num_reqs_open: company.num_reqs_open || null,
        geographies: company.geographies || null,
        years_in_business: company.years_in_business || null,
        revenues: company.revenues || null,
      });

      toast.success(editingLocationIndex === -1 ? 'Location added' : 'Location updated');
      setEditLocationOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to update location');
    } finally {
      setSaving(false);
    }
  }, [editingLocation, editingLocationIndex, company, companyId, loadData]);

  const handleDeleteLocation = useCallback(async () => {
    const ok = await msg.confirm(`Delete location "${editingLocation?.name}"?`, 'This cannot be undone.', { destructive: true, confirmLabel: 'Delete' });
    if (!ok) return;
    setSaving(true);
    try {
      const updatedLocations = (company?.locations || []).filter((_, i) => i !== editingLocationIndex);
      await axios.put(`${API}/companies/${companyId}`, {
        name: company.name,
        website: company.website || null,
        locations: updatedLocations,
        ats_used: company.ats_used || null,
        ai_voice_using: company.ai_voice_using || null,
        other_ai_tools: company.other_ai_tools || null,
        company_type: company.company_type || 'potential',
        total_revenue: company.total_revenue || null,
        total_jobs: company.total_jobs || null,
        company_notes: company.company_notes || null,
        num_recruiters: company.num_recruiters || null,
        num_reqs_open: company.num_reqs_open || null,
        geographies: company.geographies || null,
        years_in_business: company.years_in_business || null,
        revenues: company.revenues || null,
      });
      toast.success('Location deleted');
      setEditLocationOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to delete location');
    } finally {
      setSaving(false);
    }
  }, [editingLocation, editingLocationIndex, company, companyId, loadData, msg]);

  return {
    editLocationOpen,
    setEditLocationOpen,
    editingLocation,
    setEditingLocation,
    editingLocationIndex,
    saving,
    openEditLocation,
    openAddLocation,
    handleSaveLocation,
    handleDeleteLocation
  };
};

/**
 * Hook for tech stack inline editing
 */
export const useTechStackEditor = (company, companyId, loadData) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedAtsUsed, setEditedAtsUsed] = useState('');
  const [editedAiVoiceUsing, setEditedAiVoiceUsing] = useState('');
  const [editedOtherAiTools, setEditedOtherAiTools] = useState('');
  const [editedCompanyType, setEditedCompanyType] = useState('potential');
  const [editedTotalRevenue, setEditedTotalRevenue] = useState('');
  const [editedTotalJobs, setEditedTotalJobs] = useState('');
  const [editedCompanyNotes, setEditedCompanyNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const startEditing = useCallback(() => {
    setEditedAtsUsed(company?.ats_used || '');
    setEditedAiVoiceUsing(company?.ai_voice_using || '');
    setEditedOtherAiTools(company?.other_ai_tools || '');
    setEditedCompanyType(company?.company_type || 'potential');
    setEditedTotalRevenue(company?.total_revenue ? formatShorthand(company.total_revenue) : '');
    setEditedTotalJobs(company?.total_jobs ? formatShorthand(company.total_jobs) : '');
    setEditedCompanyNotes(company?.company_notes || '');
    setIsEditing(true);
  }, [company]);

  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    setEditedAtsUsed('');
    setEditedAiVoiceUsing('');
    setEditedOtherAiTools('');
    setEditedCompanyType('potential');
    setEditedTotalRevenue('');
    setEditedTotalJobs('');
    setEditedCompanyNotes('');
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/companies/${companyId}`, {
        name: company.name,
        locations: company.locations || [],
        website: company.website || null,
        ats_used: editedAtsUsed || null,
        ai_voice_using: editedAiVoiceUsing || null,
        other_ai_tools: editedOtherAiTools || null,
        company_type: editedCompanyType || 'potential',
        total_revenue: parseShorthandValue(editedTotalRevenue),
        total_jobs: parseShorthandValue(editedTotalJobs),
        company_notes: editedCompanyNotes || null
      });
      toast.success('Company updated successfully');
      setIsEditing(false);
      loadData();
    } catch (error) {
      toast.error('Failed to update company');
    } finally {
      setSaving(false);
    }
  }, [company, companyId, editedAtsUsed, editedAiVoiceUsing, editedOtherAiTools, editedCompanyType, editedTotalRevenue, editedTotalJobs, editedCompanyNotes, loadData]);

  return {
    isEditing,
    saving,
    editedAtsUsed,
    setEditedAtsUsed,
    editedAiVoiceUsing,
    setEditedAiVoiceUsing,
    editedOtherAiTools,
    setEditedOtherAiTools,
    editedCompanyType,
    setEditedCompanyType,
    editedTotalRevenue,
    setEditedTotalRevenue,
    editedTotalJobs,
    setEditedTotalJobs,
    editedCompanyNotes,
    setEditedCompanyNotes,
    startEditing,
    cancelEditing,
    save
  };
};

/**
 * Hook for website inline editing
 */
export const useWebsiteEditor = (company, companyId, loadData) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedWebsite, setEditedWebsite] = useState('');
  const [saving, setSaving] = useState(false);

  const startEditing = useCallback(() => {
    setEditedWebsite(company?.website || '');
    setIsEditing(true);
  }, [company]);

  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    setEditedWebsite('');
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/companies/${companyId}`, {
        name: company.name,
        locations: company.locations || [],
        website: editedWebsite || null,
        ats_used: company.ats_used || null,
        ai_voice_using: company.ai_voice_using || null,
        other_ai_tools: company.other_ai_tools || null,
        company_type: company.company_type || 'potential',
        total_revenue: company.total_revenue || null,
        total_jobs: company.total_jobs || null
      });
      toast.success('Website updated');
      setIsEditing(false);
      loadData();
    } catch (error) {
      toast.error('Failed to update website');
    } finally {
      setSaving(false);
    }
  }, [company, companyId, editedWebsite, loadData]);

  return {
    isEditing,
    editedWebsite,
    setEditedWebsite,
    saving,
    startEditing,
    cancelEditing,
    save
  };
};

/**
 * Hook for lead management
 */
export const useLeadManager = (companyId, companyName, selectedLocationId, loadData) => {
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [addLeadMode, setAddLeadMode] = useState('new'); // 'new' | 'existing'
  const [addLeadForm, setAddLeadForm] = useState({
    first_name: '',
    last_name: '',
    title: '',
    work_email: '',
    mobile_phone: '',
    work_direct_phone: '',
    status: 'new',
    location_id: ''
  });
  const [saving, setSaving] = useState(false);

  // Existing lead linking
  const [allLeads, setAllLeads] = useState([]);
  const [existingLeadsSearch, setExistingLeadsSearch] = useState('');
  const [selectedExistingLead, setSelectedExistingLead] = useState(null);
  const [linkingSaving, setLinkingSaving] = useState(false);
  const [loadingLeads, setLoadingLeads] = useState(false);

  const fetchAllLeads = useCallback(async () => {
    setLoadingLeads(true);
    try {
      const res = await axios.get(`${API}/leads?limit=2000`);
      const leads = (res.data.items || []).filter(l => l.company_id !== companyId);
      setAllLeads(leads);
    } catch (e) {
      toast.error('Failed to load leads');
    } finally {
      setLoadingLeads(false);
    }
  }, [companyId]);

  const filteredExistingLeads = allLeads.filter(l => {
    if (!existingLeadsSearch.trim()) return true;
    const q = existingLeadsSearch.toLowerCase();
    return (
      `${l.first_name || ''} ${l.last_name || ''}`.toLowerCase().includes(q) ||
      (l.work_email || '').toLowerCase().includes(q) ||
      (l.title || '').toLowerCase().includes(q) ||
      (l.company_name || '').toLowerCase().includes(q)
    );
  });

  const openAddLeadDialog = useCallback(() => {
    setAddLeadForm({
      first_name: '',
      last_name: '',
      title: '',
      work_email: '',
      mobile_phone: '',
      work_direct_phone: '',
      status: 'new',
      location_id: selectedLocationId || ''
    });
    setAddLeadMode('new');
    setExistingLeadsSearch('');
    setSelectedExistingLead(null);
    setAllLeads([]);
    setAddLeadOpen(true);
  }, [selectedLocationId]);

  const handleAddLead = useCallback(async () => {
    if (!addLeadForm.first_name) {
      toast.error('First name is required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/leads`, {
        ...addLeadForm,
        work_email: addLeadForm.work_email?.trim() || null,
        personal_email: addLeadForm.personal_email?.trim() || null,
        mobile_phone: addLeadForm.mobile_phone?.trim() || null,
        work_direct_phone: addLeadForm.work_direct_phone?.trim() || null,
        location_id: addLeadForm.location_id || null,
        company_id: companyId,
        company_name: companyName
      });
      toast.success('Lead added successfully');
      setAddLeadOpen(false);
      loadData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = Array.isArray(detail)
        ? detail.map(d => d.msg).join(', ')
        : (detail || 'Failed to add lead');
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }, [addLeadForm, companyId, companyName, loadData]);

  const handleLinkExistingLead = useCallback(async () => {
    if (!selectedExistingLead) return;
    setLinkingSaving(true);
    try {
      await axios.put(`${API}/leads/${selectedExistingLead.id}`, {
        company_id: companyId,
        company_name: companyName,
      });
      toast.success(`${selectedExistingLead.first_name} ${selectedExistingLead.last_name || ''} linked to company`);
      setAddLeadOpen(false);
      loadData();
    } catch (e) {
      toast.error('Failed to link lead');
    } finally {
      setLinkingSaving(false);
    }
  }, [selectedExistingLead, companyId, companyName, loadData]);

  return {
    addLeadOpen,
    setAddLeadOpen,
    addLeadMode,
    setAddLeadMode,
    addLeadForm,
    setAddLeadForm,
    saving,
    openAddLeadDialog,
    handleAddLead,
    // existing lead linking
    allLeads,
    fetchAllLeads,
    existingLeadsSearch,
    setExistingLeadsSearch,
    filteredExistingLeads,
    selectedExistingLead,
    setSelectedExistingLead,
    linkingSaving,
    loadingLeads,
    handleLinkExistingLead,
  };
};
